import type { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import styled from 'styled-components';
import { Utils } from '@livecontrol/core-ui';

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
`;

const Box = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.6);

  .unit {
    text-align: center;
    font-weight: 700;
  }

  .value {
    font-size: 16px;
    line-height: 1;
  }

  .label {
    margin-top: 4px;
    font-size: 8px;
    text-transform: uppercase;
    opacity: 0.6;
  }

  .sep {
    font-size: 16px;
    font-weight: 700;
    align-self: flex-start;
  }
`;

const format = (n: number): string => String(Math.max(0, Math.round(n))).padStart(2, '0');

export const Countdown = ({ start }: { start: DateTime }): ReactElement | null => {
  const { days, hours, minutes, seconds, complete } = Utils.useCountdown(start);

  if (complete) {
    return null;
  }

  const units = [
    { value: days, label: days === 1 ? 'day' : 'days' },
    { value: hours, label: hours === 1 ? 'hour' : 'hours' },
    { value: minutes, label: minutes === 1 ? 'min' : 'mins' },
    { value: seconds, label: seconds === 1 ? 'sec' : 'secs' }
  ];

  return (
    <Overlay>
      <Box>
        {units.map((unit, index) => (
          <Fragment key={unit.label}>
            {index > 0 && <span className='sep'>:</span>}
            <div className='unit'>
              <div className='value'>{format(unit.value)}</div>
              <div className='label'>{unit.label}</div>
            </div>
          </Fragment>
        ))}
      </Box>
    </Overlay>
  );
};
